import { MapShape } from '@larki/mp/doc';
import { isDerivedShape } from '@larki/mp/lib/shape';
import { shapeLib } from '../mp/lib/shape';
const { extractShapeGeometry } = shapeLib;

import { arrayLib } from '../mp/lib/array';
import axios from 'axios';
import {
  _2dMapCombinedQuote,
  Locality,
  LocalityApiResponse,
} from '@larki/shared/type/order.v2';
import { transformCoverageLayers } from '@larki/shared/utilities';
import { CoverageDetailed } from '@larki/shared/type';

const transformRequest = axios.defaults.transformRequest;
export const workerLarkiApi = (sessionToken: string) =>
  axios.create({
    baseURL: process.env.LARKI_API_URL + '/api',
    headers: {
      'Content-Type': 'application/json',
    },
    transformRequest: [
      (data, headers) => {
        sessionToken ??= '<not logged in>'; // trigger usual unauthenticated error path
        if (headers) {
          headers['Authorization'] = 'Bearer ' + sessionToken;
          if (process.env.BUILD_NUMBER) {
            headers['Larki-Version'] = process.env.BUILD_NUMBER;
          }
        }
        return data;
      },
      ...(Array.isArray(transformRequest)
        ? transformRequest
        : transformRequest
          ? [transformRequest]
          : []), // don't lose axios' default JSON => string behaviour
    ],
  });

export const quote2dMapWorker = async ({
  shapes,
  sessionToken,
  placeGeometry,
}: {
  shapes: MapShape[];
  sessionToken: string;
  placeGeometry?: GeoJSON.Point | null;
}) => {
  const api = workerLarkiApi(sessionToken);

  const [coverageResponse, localityResponse] = await Promise.all([
    api.post<CoverageDetailed>('/engine/coverage/detailed', {
      shapes: shapes
        .map((shape) => {
          const geometry = extractShapeGeometry(shape);
          if (!geometry) return null;
          return {
            id: shape.id,
            geometry,
            category_name: shape.dataType,
            parent_shape_id: isDerivedShape(shape) ? shape.baseShapeId : null,
          };
        })
        .filter(arrayLib.filterNonNull),
    }),
    placeGeometry
      ? api.post<LocalityApiResponse | null>('/engine/locality', {
          point: placeGeometry,
        })
      : Promise.resolve({ data: null } as { data: LocalityApiResponse | null }),
  ]);

  const coverageDetailed = coverageResponse.data;
  const localityResult = localityResponse.data;

  const locality: Locality = {
    name: localityResult?.name ?? null,
    type: localityResult?.type ?? null,
    distanceInMeters: localityResult?.distance_from_centre_in_meters ?? null,
  };

  const layers = transformCoverageLayers(coverageDetailed).filter(
    arrayLib.filterNonNull
  );

  return {
    locality,
    layers,
    coverage: coverageDetailed,
  };
};

export type Quote2dMapWorkerInput = Parameters<typeof quote2dMapWorker>;
export type Quote2dMapWorkerOutput = Awaited<
  ReturnType<typeof quote2dMapWorker>
>;
